// Spacer ring stock + allocation logic.
// Pure (no React) — extracted from SpacerVisualization.jsx for clarity.

export const DEFAULT_SPACER_STOCK = {
  20: 8,
  19: 8,
  11: 20,
  9: 8,
  5: 8
};

export const AVAILABLE_SPACERS = Object.keys(DEFAULT_SPACER_STOCK).map(Number).sort((a, b) => b - a);

// Station counts for allocation
export const STATION_COUNTS = {
  sideRolling: 6,  // Stations 5-10
  lipRolling: 3,   // Stations 2-4
  feeding: 1       // Feeding Station
};

// Fixed reference distance from right-hand starting point (mm)
export const BOTTOM_TUBE_REFERENCE = 110;

// Find all possible combinations for a target value (up to a limit)
export const findAllCombinations = (targetMm, maxCombinations = 50) => {
  const target = Math.round(targetMm);
  
  const combinations = [];
  
  // Use BFS to find combinations
  const queue = [{ sum: 0, spacers: [], counts: {} }];
  const visited = new Set();
  
  while (queue.length > 0 && combinations.length < maxCombinations) {
    const { sum, spacers, counts } = queue.shift();
    
    if (sum === target) {
      combinations.push({
        spacers: [...spacers],
        counts: { ...counts },
        remainder: 0
      });
      continue;
    }
    
    if (sum > target) continue;
    
    for (const spacer of AVAILABLE_SPACERS) {
      const newSum = sum + spacer;
      if (newSum > target) continue;
      
      const newSpacers = [...spacers, spacer].sort((a, b) => b - a);
      const key = newSpacers.join(',');
      
      if (!visited.has(key)) {
        visited.add(key);
        const newCounts = { ...counts };
        newCounts[spacer] = (newCounts[spacer] || 0) + 1;
        queue.push({ sum: newSum, spacers: newSpacers, counts: newCounts });
      }
    }
  }
  
  // Sort by fewest spacers, then by remainder
  combinations.sort((a, b) => a.spacers.length - b.spacers.length);
  
  // If no exact match found, find closest using DP
  if (combinations.length === 0) {
    const dp = new Array(target + 1).fill(null);
    dp[0] = { counts: {}, total: 0 };
    
    for (let sum = 1; sum <= target; sum++) {
      for (const spacer of AVAILABLE_SPACERS) {
        if (spacer <= sum && dp[sum - spacer] !== null) {
          const prevCounts = dp[sum - spacer].counts;
          const newCounts = { ...prevCounts };
          newCounts[spacer] = (newCounts[spacer] || 0) + 1;
          const newTotal = dp[sum - spacer].total + 1;
          
          if (dp[sum] === null || newTotal < dp[sum].total) {
            dp[sum] = { counts: newCounts, total: newTotal };
          }
        }
      }
    }
    
    // Find best achievable sum
    for (let sum = target; sum >= 0; sum--) {
      if (dp[sum] !== null) {
        const spacers = [];
        for (const [size, count] of Object.entries(dp[sum].counts)) {
          for (let i = 0; i < count; i++) spacers.push(Number(size));
        }
        combinations.push({
          spacers: spacers.sort((a, b) => b - a),
          counts: dp[sum].counts,
          remainder: target - sum
        });
        break;
      }
    }
  }
  
  return combinations;
};

// Check if a combination fits within available stock (multiplied by station count)
const fitsInStock = (counts, stationCount, availableStock) => {
  for (const [size, needed] of Object.entries(counts)) {
    const totalNeeded = needed * stationCount;
    if (totalNeeded > (availableStock[size] || 0)) {
      return false;
    }
  }
  return true;
};

// Deduct spacers from stock
const deductFromStock = (counts, stationCount, stock) => {
  const newStock = { ...stock };
  for (const [size, needed] of Object.entries(counts)) {
    newStock[size] = (newStock[size] || 0) - (needed * stationCount);
  }
  return newStock;
};

// Find best combination that fits within stock
const findBestFittingCombination = (targetMm, stationCount, availableStock) => {
  const target = Math.round(targetMm);
  const combinations = findAllCombinations(target);
  
  // First try to find exact match that fits in stock
  for (const combo of combinations) {
    if (combo.remainder === 0 && fitsInStock(combo.counts, stationCount, availableStock)) {
      return combo;
    }
  }
  
  // If no exact match fits, use DP with stock constraints
  // For each spacer, max we can use per station
  const maxPerStation = {};
  for (const size of AVAILABLE_SPACERS) {
    maxPerStation[size] = Math.floor((availableStock[size] || 0) / stationCount);
  }
  
  // DP with stock constraints
  const dp = new Array(target + 1).fill(null);
  dp[0] = { counts: {}, total: 0 };
  
  for (let sum = 1; sum <= target; sum++) {
    for (const size of AVAILABLE_SPACERS) {
      if (size <= sum && dp[sum - size] !== null) {
        const prevCounts = dp[sum - size].counts;
        const prevCount = prevCounts[size] || 0;
        
        // Check stock constraint
        if (prevCount + 1 <= maxPerStation[size]) {
          const newCounts = { ...prevCounts, [size]: prevCount + 1 };
          const newTotal = dp[sum - size].total + 1;
          
          if (dp[sum] === null || newTotal < dp[sum].total) {
            dp[sum] = { counts: newCounts, total: newTotal };
          }
        }
      }
    }
  }
  
  // Find best achievable sum (exact match first)
  if (dp[target] !== null) {
    const spacers = [];
    for (const [size, count] of Object.entries(dp[target].counts)) {
      for (let i = 0; i < count; i++) spacers.push(Number(size));
    }
    return {
      spacers: spacers.sort((a, b) => b - a),
      counts: dp[target].counts,
      remainder: 0
    };
  }
  
  // Find closest sum below target
  for (let sum = target - 1; sum >= 0; sum--) {
    if (dp[sum] !== null) {
      const spacers = [];
      for (const [size, count] of Object.entries(dp[sum].counts)) {
        for (let i = 0; i < count; i++) spacers.push(Number(size));
      }
      return {
        spacers: spacers.sort((a, b) => b - a),
        counts: dp[sum].counts,
        remainder: target - sum
      };
    }
  }
  
  return { spacers: [], counts: {}, remainder: target };
};

// Global allocation across all stations
export const allocateSpacersGlobally = (spacerValues, spacerStock) => {
  const { bottom_5_10, bottom_2_4, feedingBottom } = spacerValues;
  let remainingStock = { ...spacerStock };
  
  // Find all exact combinations for a target
  const findExactCombinations = (target) => {
    if (target <= 0) return [];
    const combinations = [];
    const generate = (remaining, minSize, currentCounts) => {
      if (remaining === 0) {
        combinations.push({ ...currentCounts });
        return;
      }
      if (remaining < 0) return;
      for (const size of AVAILABLE_SPACERS) {
        if (size > minSize || size > remaining) continue;
        const newCounts = { ...currentCounts };
        newCounts[size] = (newCounts[size] || 0) + 1;
        generate(remaining - size, size, newCounts);
      }
    };
    generate(target, Math.max(...AVAILABLE_SPACERS), {});
    return combinations;
  };
  
  // Find best partial combo with remaining stock
  const findBestPartial = (target, usedStock) => {
    const dp = new Array(target + 1).fill(null);
    dp[0] = { counts: {}, total: 0 };
    for (let sum = 1; sum <= target; sum++) {
      for (const size of AVAILABLE_SPACERS) {
        if (size > sum || dp[sum - size] === null) continue;
        const prevCounts = dp[sum - size].counts;
        const currentCount = prevCounts[size] || 0;
        const available = (remainingStock[size] || 0) - (usedStock[size] || 0);
        if (currentCount >= available) continue;
        const newCounts = { ...prevCounts, [size]: currentCount + 1 };
        const newTotal = dp[sum - size].total + 1;
        if (dp[sum] === null || newTotal < dp[sum].total) {
          dp[sum] = { counts: newCounts, total: newTotal };
        }
      }
    }
    for (let sum = target; sum >= 0; sum--) {
      if (dp[sum] !== null) return { counts: dp[sum].counts, remainder: target - sum };
    }
    return { counts: {}, remainder: target };
  };
  
  // Assign mixed combinations to stations
  const assignMixed = (target, stationCount) => {
    const t = Math.round(target);
    if (t <= 0 || stationCount <= 0) return Array(stationCount).fill({ counts: {}, remainder: 0 });
    
    const exactCombos = findExactCombinations(t);
    const scoredCombos = exactCombos.map(counts => {
      let score = 0, total = 0;
      for (const [size, count] of Object.entries(counts)) {
        score += count * (1 / ((remainingStock[size] || 0) + 1));
        total += count;
      }
      return { counts, score, total };
    }).sort((a, b) => a.total - b.total || a.score - b.score);
    
    const assignment = [];
    const usedStock = {};
    
    for (let i = 0; i < stationCount; i++) {
      let assigned = false;
      for (const { counts } of scoredCombos) {
        let fits = true;
        for (const [size, count] of Object.entries(counts)) {
          if ((usedStock[size] || 0) + count > (remainingStock[size] || 0)) {
            fits = false;
            break;
          }
        }
        if (fits) {
          assignment.push({ counts, remainder: 0 });
          for (const [size, count] of Object.entries(counts)) {
            usedStock[size] = (usedStock[size] || 0) + count;
          }
          assigned = true;
          break;
        }
      }
      if (!assigned) {
        const partial = findBestPartial(t, usedStock);
        assignment.push(partial);
        for (const [size, count] of Object.entries(partial.counts)) {
          usedStock[size] = (usedStock[size] || 0) + count;
        }
      }
    }
    return assignment;
  };
  
  // Consolidate into groups (used for the legacy summary view; per-station
  // arrays are returned separately so each broken-out station row can render
  // ITS OWN allocation rather than the grouped "×N stations" totals).
  const consolidate = (assignments) => {
    const groups = new Map();
    for (const { counts, remainder } of assignments) {
      const key = JSON.stringify(counts) + '|' + remainder;
      if (!groups.has(key)) groups.set(key, { counts, remainder, count: 0 });
      groups.get(key).count++;
    }
    return Array.from(groups.values());
  };
  
  // Stations 5-7 always share one identical config — pick a combo that can be
  // replicated `copies`× from stock (each copy depletes its own ring set).
  const assignUniform = (target, copies) => {
    const t = Math.round(target);
    if (t <= 0) return Array(copies).fill({ counts: {}, remainder: 0 });
    const exactCombos = findExactCombinations(t);
    const scored = exactCombos.map(counts => {
      let score = 0, total = 0;
      for (const [size, count] of Object.entries(counts)) {
        score += count * (1 / ((remainingStock[size] || 0) + 1));
        total += count;
      }
      return { counts, score, total };
    }).sort((a, b) => a.total - b.total || a.score - b.score);
    for (const { counts } of scored) {
      let fits = true;
      for (const [size, count] of Object.entries(counts)) {
        if (count * copies > (remainingStock[size] || 0)) { fits = false; break; }
      }
      if (fits) return Array(copies).fill({ counts, remainder: 0 });
    }
    // No exact combo fits ×copies — best identical partial from stock/copies
    const dp = new Array(t + 1).fill(null);
    dp[0] = { counts: {}, total: 0 };
    for (let sum = 1; sum <= t; sum++) {
      for (const size of AVAILABLE_SPACERS) {
        if (size > sum || dp[sum - size] === null) continue;
        const prevCounts = dp[sum - size].counts;
        const currentCount = prevCounts[size] || 0;
        const available = Math.floor((remainingStock[size] || 0) / copies);
        if (currentCount >= available) continue;
        const newCounts = { ...prevCounts, [size]: currentCount + 1 };
        const newTotal = dp[sum - size].total + 1;
        if (dp[sum] === null || newTotal < dp[sum].total) {
          dp[sum] = { counts: newCounts, total: newTotal };
        }
      }
    }
    for (let sum = t; sum >= 0; sum--) {
      if (dp[sum] !== null) return Array(copies).fill({ counts: dp[sum].counts, remainder: t - sum });
    }
    return Array(copies).fill({ counts: {}, remainder: t });
  };

  // Priority 1: Side Rolling (6 stations).
  // Stations 5-7 are physically identical → one shared config, three ring
  // sets (each deducted from stock). Stations 8-10 are assigned individually.
  const tripleAssign = assignUniform(bottom_5_10, 3);
  for (const { counts } of tripleAssign) {
    for (const [size, count] of Object.entries(counts)) {
      remainingStock[size] = (remainingStock[size] || 0) - count;
    }
  }
  const restAssign = assignMixed(bottom_5_10, 3); // stations 8, 9, 10
  for (const { counts } of restAssign) {
    for (const [size, count] of Object.entries(counts)) {
      remainingStock[size] = (remainingStock[size] || 0) - count;
    }
  }
  const sideAssign = [...tripleAssign, ...restAssign];
  
  // Priority 2: Lip Rolling (3 stations)
  let lipAssign = [];
  if (bottom_2_4 > 0) {
    lipAssign = assignMixed(bottom_2_4, STATION_COUNTS.lipRolling);
    for (const { counts } of lipAssign) {
      for (const [size, count] of Object.entries(counts)) {
        remainingStock[size] = (remainingStock[size] || 0) - count;
      }
    }
  }
  
  // Priority 3: Feeding Station
  //   - With lips: only Station 1 is used as feeding (count = 1)
  //   - Without lips: Stations 1 & 2 are IDLE (skipped — no rings), Stations
  //     3 & 4 act as feeding (count = 2). This matches the physical setup
  //     where the first two roller pairs are pass-through when no lip rolling
  //     is required.
  const feedingStationCount = bottom_2_4 > 0 ? 1 : 2;
  const feedAssign = assignMixed(feedingBottom, feedingStationCount);
  
  return {
    // Consolidated (×N) — kept for backwards compatibility / summary views.
    sideRolling: consolidate(sideAssign),
    lipRolling: consolidate(lipAssign),
    feeding: consolidate(feedAssign),
    // Per-station arrays — one entry per physical station, in station order.
    // Use these when rendering individual labeled station rows so totals
    // don't get inflated by the consolidation multiplier.
    perStation: {
      sideRolling: sideAssign,   // [station5, station6, ..., station10]
      lipRolling: lipAssign,     // [station2, station3, station4]
      feeding: feedAssign,       // [station1] (with lips) or [station3, station4] (no lips)
    },
  };
};
